import { OrderType } from 'hooks/useDCSTypes';
import { AGENT_ID_COOKIE_NAME } from 'services/analytics/analytics';
import { readCookie } from 'utils/Cookies';
import type { GrowthLeadRequest } from 'services/shop/checkout';
import type { ICartDetailModel, ICheckoutModel, IProductModel } from 'components/Cart/models';
import { Installments } from 'components/Cart/OrderSummary/types';
import { CART_PRODUCT_IDS } from 'components/Cart/Cart.const';

const SUBMIT_CART_ACTIVITY_TYPE = 'formSubmit_c';

export const defaultErrorState = {
  hasApiError: false,
  hasDuplicateAccountError: false,
  hasDuplicateLeadError: false,
  isLockedOut: false,
};

type UserCheckoutInfo = {
  firstName: string;
  lastName: string;
  businessName: string;
  phoneNumber: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
};

type SubmitCartType = {
  cart: ICartDetailModel;
  checkout: ICheckoutModel;
  userInfo: UserCheckoutInfo;
  emailAddress: string;
  resellerId: string;
  marketingInfo: { [key: string]: any };
  selectedPurchaseType: Installments;
  subscriberRefId?: string;
};

const buildShippingAddress = (userInfo: UserCheckoutInfo) => {
  const { city, address1, address2, zip, state } = userInfo;
  if (!city || !address1 || !state || !zip) return undefined;

  return {
    city,
    lineOne: address1,
    lineTwo: address2,
    state,
    zipCode: zip,
  };
};

export const generateCartRequestObject = ({
  cart,
  checkout,
  userInfo,
  emailAddress,
  resellerId,
  marketingInfo,
  selectedPurchaseType,
  subscriberRefId,
}: SubmitCartType) => {
  if (!userInfo) throw new Error('Cannot submit cart. Missing: User Information');
  if (!emailAddress) throw new Error('Cannot submit cart. Missing: User Email');
  if (!cart.softwarePlan) throw new Error('Cannot submit cart. Missing: Software Plan');
  if (!resellerId) throw new Error('Cannot submit cart. Missing: Reseller UUID');

  const chainAgentId = readCookie(AGENT_ID_COOKIE_NAME);
  if (!chainAgentId) throw new Error('Cannot submit cart. Missing: Chain Agent ID');

  const bundleId = cart.cartMetadata?.id;
  const shippingAddress = buildShippingAddress(userInfo);

  const requestObject: GrowthLeadRequest = {
    email: emailAddress.trim(),
    firstName: userInfo.firstName.trim(),
    lastName: userInfo.lastName.trim(),
    businessName: userInfo.businessName,
    phoneNumber: userInfo.phoneNumber,
    marketingInfo,
    chainAgentId,
    activityType: SUBMIT_CART_ACTIVITY_TYPE,
    resellerUuid: resellerId,
    order: {
      planId: cart.softwarePlan.id,
      shippingAddress,
      items: [],
    },
    subscriberRefId,
  };

  if (checkout.promo.applied) requestObject.order!.promoCode = checkout.promo.applied;
  if (bundleId && cart.orderType === OrderType.BUNDLE) requestObject.order!.bundleId = bundleId;

  const selectedPurchaseTypeInCart = (product: IProductModel) => {
    if (product.id === CART_PRODUCT_IDS.NO_HARDWARE) return Installments.O;

    return product.installmentPlans?.SL36 && product.installmentPlans?.SL48
      ? selectedPurchaseType
      : Installments.P;
  };

  const pushProductToOrder = (product: IProductModel) => {
    requestObject.order!.items.push({
      fdmpId: product.fdmpId,
      digitalCatalogId: product.id,
      purchaseType: selectedPurchaseTypeInCart(product),
      quantity: product.quantity,
    });
  };

  cart.cartMetadata?.hardware.forEach(pushProductToOrder);
  cart.products.forEach(pushProductToOrder);

  // checks if one of the products added to cart has subscription pricing
  const hasSubscriptionPurchaseType = requestObject.order?.items.some(
    productItem =>
      productItem.purchaseType === Installments.SL36 || productItem.purchaseType === Installments.SL48,
  );

  // if one of the products has subscription purchase type, then promo is unavailable while creating leads
  if (hasSubscriptionPurchaseType) {
    requestObject.order!.promoCode = undefined;
  }

  return requestObject;
};
